import React, { useContext } from "react";
import { DefaultButton, MessageBar, MessageBarType, Spinner, SpinnerSize } from "@fluentui/react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import DocumentTitle from "react-document-title";
import styled from "styled-components";
import SuitabilityTest from "@/components/SuitabilityTests/SuitabilityTest";
import { SuitabilityQuestionnaireChangedMessage } from "@/screen/ContractManagement/helpers/suitabilityTestValidity";
import { SuitabilityTestRenewalConfirmPath } from "@/screen/Identities/identitiesRoutes";
import { BarCont, BgBar, Cont, HelperStyle, MainCont, Title, TitleSubtitleRow } from "@/screen/Identities/Styles/IdentitiesMainScreenStyles";
import { formatIsoDateTimeToDdMmYyyy } from "@/utils/DateFormatter";
import { useSuitabilityTestRenewalController } from "@/screen/Identities/SuitabilityTestRenewal/useSuitabilityTestRenewalController";
import type { SuitabilityRenewalReason } from "@/screen/Identities/SuitabilityTestRenewal/suitabilityRenewalView";

const RenewalCopy: Record<SuitabilityRenewalReason, {title: string; intro: string}> =
{
    age:
    {
        title: "Renovar test de idoneidad",
        intro: "Tu test de idoneidad ha caducado. Revisa tus respuestas, actualízalas si algo ha cambiado y fírmalo",
    },
    version:
    {
        title: "Actualizar test de idoneidad",
        intro: "Hay una versión nueva del cuestionario. Revisa tus respuestas, actualízalas si algo ha cambiado y fírmalo",
    },
    voluntary:
    {
        title: "Actualizar test de idoneidad",
        intro: "Puedes actualizar tu test de idoneidad. Revisa tus respuestas, cámbialas si algo ha cambiado y fírmalo",
    },
};

const RenewalIntro = styled.p`
    margin: 16px 0 8px;
    font-size: 16px;
    line-height: 24px;
    color: #5f7172;
`;

const RenewalExpiry = styled.p`
    margin: 0 0 16px;
    font-size: 14px;
    font-weight: 600;
    color: #2a4143;
`;

const SuitabilityTestRenewalScreen: React.FC = () =>
{
    const themeContext = useContext(ThemeContext);
    const
    {
        userIdentityId,
        displayName,
        isLoading,
        loadError,
        view,
        goBack,
        leaveBecauseOfLimit,
    } = useSuitabilityTestRenewalController();

    if(!userIdentityId)
    {
        return null;
    }

    const showForm = !isLoading && !loadError && view && !view.redirectToList && view.reason != null;
    const copy = view?.reason ? RenewalCopy[view.reason] : RenewalCopy.age;
    const expiresLabel = view?.reason === "age" && view.expiresAt ? formatIsoDateTimeToDdMmYyyy(view.expiresAt) : "";

    return(
        <DocumentTitle title={`IronIA - ${copy.title}`}>
            <React.Fragment>
                <HelperStyle />
                <Cont theme={themeContext}>
                    <MainCont>
                        <Title>
                            <h1>{copy.title}</h1>
                        </Title>
                        {displayName &&
                        (
                            <TitleSubtitleRow>
                                <h2>{displayName}</h2>
                            </TitleSubtitleRow>
                        )}
                        <BarCont>
                            <BgBar />
                        </BarCont>
                        <RenewalIntro>
                            {copy.intro}
                        </RenewalIntro>
                        {expiresLabel !== "" && expiresLabel !== "—" &&
                        (
                            <RenewalExpiry>Caducó el {expiresLabel}</RenewalExpiry>
                        )}
                        {showForm && view.questionnaireChanged &&
                        (
                            <MessageBar messageBarType={MessageBarType.warning} isMultiline={true} styles={{root: {marginBottom: "16px"}}}>
                                {SuitabilityQuestionnaireChangedMessage}
                            </MessageBar>
                        )}
                        {loadError &&
                        (
                            <React.Fragment>
                                <MessageBar messageBarType={MessageBarType.error} isMultiline={true} styles={{root: {marginBottom: "16px"}}}>
                                    {loadError}
                                </MessageBar>
                                <DefaultButton className="button tiny-secondary-button" onClick={goBack}>
                                    Volver
                                </DefaultButton>
                            </React.Fragment>
                        )}
                        {!showForm && !loadError &&
                        (
                            <div style={{textAlign: "center", padding: "40px"}}>
                                <Spinner size={SpinnerSize.large} />
                            </div>
                        )}
                        {showForm &&
                        (
                            <SuitabilityTest
                                userIdentityId={userIdentityId}
                                confirmUrl={SuitabilityTestRenewalConfirmPath}
                                initialAnswers={view.initialAnswers}
                                onLimitReached={leaveBecauseOfLimit}
                            />
                        )}
                    </MainCont>
                </Cont>
            </React.Fragment>
        </DocumentTitle>
    );
};

export default SuitabilityTestRenewalScreen;
